import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "v-check-child" }
const _hoisted_2 = { class: "v-check-child__header" }
const _hoisted_3 = { class: "left" }
const _hoisted_4 = { class: "right" }
const _hoisted_5 = { class: "v-check-child__body" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "date-label" }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = {
  key: 0,
  class: "icon"
}

import { ChevronDown, LinkExternal02, SearchMd } from "utruckin-icons";
import CommonSearch from "@/components/common/CommonSearch.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import CustomTab from "@/components/custom/CustomTab.vue";
import { useCheckStore } from "@/features/check/check.store";
import { IFilterEligibleTasks } from "@/features/check/check.types";
import { DateTime } from "luxon";
import CommonTable from "@/components/common/CommonTable.vue";
import { useUserStore } from "@/features/users/user.store";
import CommonStatus from "@/components/common/CommonStatus.vue";
import { TaskStatus } from "@/features/tasks/task.enum";
import CustomSearchDropdown from "@/components/custom/CustomSearchDropdown.vue";
import { IWorkType } from "@/features/work-types/work-type.types";
import { useWorkTypeStore } from "@/features/work-types/work-type.store";
import router from "@/router";


export default /*@__PURE__*/_defineComponent({
  __name: 'CheckChildView',
  setup(__props) {

const checkStore = useCheckStore();
const userStore = useUserStore();
const workTypeStore = useWorkTypeStore();
const route = useRoute();

const loading = ref<boolean>(false);
const workTypeLoading = ref<boolean>(false);
const filterPage = ref<number>(1);
const filterPerPage = ref<number>(50);
const eligibleTasks = ref<any[]>([]);
const searchQuery = ref<string>("");
const problem = ref<string>("");
const activeTab = ref<number>(0);
const tabs = [
  { label: "New tasks", value: 0 },
  { label: "Completed", value: 1 },
];
const isAdmin = ref<boolean>(false);
const workTypes = ref<IWorkType[]>([]);

const collapsedDates = ref<Record<string, boolean>>({});
const collapsedShifts = ref<Record<string, boolean>>({});

let searchFilterProblemTimer: ReturnType<typeof setTimeout> | null = null;
let filterTimeout: ReturnType<typeof setTimeout> | null = null;

const groupedByDate = computed(() => {
  const dateMap = new Map<string, Map<string, any[]>>();

  for (const task of eligibleTasks.value) {
    const date = task.created_time
      ? DateTime.fromISO(task.created_time, { zone: "utc" })
          .setZone("America/New_York")
          .toFormat("yyyy-MM-dd")
      : "Unknown";

    const shift = task.shift_name ?? "Unknown Shift";

    if (!dateMap.has(date)) {
      dateMap.set(date, new Map());
    }

    const shiftMap = dateMap.get(date)!;

    if (!shiftMap.has(shift)) {
      shiftMap.set(shift, []);
    }

    shiftMap.get(shift)!.push(task);
  }

  return Array.from(dateMap.entries()).map(([date, shiftMap]) => {
    const shiftGroups = Array.from(shiftMap.entries()).map(
      ([label, items]) => ({
        label,
        items,
      })
    );

    return { date, shiftGroups };
  });
});

const problemFilterItems = computed(() =>
  (workTypes.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
  }))
);

const toggleDate = (date: string) => {
  collapsedDates.value[date] = !collapsedDates.value[date];
};
const toggleShift = (date: string, shift: string) => {
  const key = `${date}::${shift}`;
  collapsedShifts.value[key] = !collapsedShifts.value[key];
};

const isDateCollapsed = (date: string) => collapsedDates.value[date];
const isShiftCollapsed = (date: string, shift: string) =>
  collapsedShifts.value[`${date}::${shift}`];

function datepickerToUtcDateOnly(d: string | Date | null | undefined) {
  if (!d) return "";

  const date =
    typeof d === "string"
      ? DateTime.fromISO(d, { zone: "utc" })
      : DateTime.fromJSDate(d, { zone: "utc" });

  return date.setZone("America/New_York").toFormat("HH:mm:ss");
}

const formatStatusLabel = (key: string) =>
  key.replace(/_/g, "_").replace(/\b\w/g, (c) => c.toUpperCase());

const formatToEdt = (date: any) => {
  if (!date) return "";

  return date.split("T")[1] == "11:00:00.000Z"
    ? DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d")
    : DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d, HH:mm ZZZZ");
};

const tooltipContent = (list: any[]) =>
  list
    ?.map((item) => `<p>${item.label} - ${formatToEdt(item.date)}</p>`)
    ?.join("");

const getStatus = (qaStatus: string) => {
  const key = (Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>).find(
    (k) => TaskStatus[k] === qaStatus
  );
  return key ? formatStatusLabel(key) : null;
};

const goLink = (e: any, item: any) => {
  e.stopPropagation();
  window.open(item.eld_link, "_blank")?.focus();
};

const getTasks = async () => {
  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");
  const filter: IFilterEligibleTasks = {
    page: filterPage.value,
    per_page: filterPerPage.value,
    search: searchQuery.value,
    organization_id: organizationId,
    shift_id: route.query.shift_id ? route.query.shift_id : "",
    team_id: route.query.team_id ? route.query.team_id : "",
    user_id: route.params.id,
    work_type_id: problem.value,
  };
  await checkStore.getEligibleTasks(loading, filter, (res: any) => {
    if (res) {
      eligibleTasks.value = res.items;
    }
  });
};

const searchFilterWorkType = (val: string) => {
  if (searchFilterProblemTimer) clearTimeout(searchFilterProblemTimer);
  searchFilterProblemTimer = setTimeout(async () => {
    workTypeStore.commonFilter.search = val;
    const [fetchedWorkTypes] = await Promise.all([
      workTypeStore.getWorkTypes(workTypeLoading),
    ]);

    workTypes.value = fetchedWorkTypes;
  }, 400);
};

const getProblems = async () => {
  const [fetchedWorkTypes] = await Promise.all([
    workTypeStore.getWorkTypes(workTypeLoading),
  ]);

  workTypes.value = fetchedWorkTypes;
};

onMounted(async () => {
  if (
    userStore.userProfile?.role === "admin" ||
    userStore.userProfile?.role === "super_admin"
  ) {
    isAdmin.value = true;
  }
  await getTasks();
  await getProblems();
});

watch([searchQuery, problem], () => {
  if (filterTimeout) clearTimeout(filterTimeout);
  filterTimeout = setTimeout(async () => {
    await getTasks();
  }, 300);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(CustomTab, {
          tabs: tabs,
          modelValue: activeTab.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event))
        }, null, 8, ["modelValue"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(CommonSearch, {
          modelValue: searchQuery.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
          icon: _unref(SearchMd),
          placeholder: "Search",
          clearable: ""
        }, null, 8, ["modelValue", "icon"]),
        _createVNode(CustomSearchDropdown, {
          modelValue: problem.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((problem).value = $event)),
          items: problemFilterItems.value,
          label: "Problem",
          position: "bottom",
          clearable: "",
          dropdownFullWidth: "",
          maxWidth: "400px",
          onSearch: searchFilterWorkType
        }, null, 8, ["modelValue", "items"])
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedByDate.value, (dateGroup, dateIndex) => {
        return (_openBlock(), _createElementBlock(_Fragment, { key: dateIndex }, [
          _createElementVNode("div", {
            class: _normalizeClass(["date-row", { 'is-collapsed': isDateCollapsed(dateGroup.date) }]),
            onClick: ($event: any) => (toggleDate(dateGroup.date))
          }, [
            _createElementVNode("span", _hoisted_7, _toDisplayString(dateGroup.date), 1),
            _createElementVNode("span", {
              class: _normalizeClass(["date-chevron", { 'is-collapsed': isDateCollapsed(dateGroup.date) }])
            }, [
              _createVNode(_unref(ChevronDown), { size: 20 })
            ], 2)
          ], 10, _hoisted_6),
          (!isDateCollapsed(dateGroup.date))
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(dateGroup.shiftGroups, (shiftGroup, shiftIndex) => {
                return (_openBlock(), _createBlock(CommonTable, {
                  key: shiftIndex,
                  headers: _unref(checkStore).checkChildHeaders,
                  groups: [
                {
                  label: shiftGroup.label,
                  items: shiftGroup.items,
                },
              ],
                  loading: loading.value,
                  collapsed: isShiftCollapsed(dateGroup.date, shiftGroup.label),
                  onToggleGroup: ($event: any) => (toggleShift(dateGroup.date, shiftGroup.label)),
                  dateIndex: dateIndex,
                  shiftIndex: shiftIndex,
                  isAdmin: isAdmin.value,
                  withoutHeader: shiftIndex > 0
                }, {
                  id: _withCtx(({ index }) => [
                    _createElementVNode("span", null, _toDisplayString(shiftGroup.items.length - index), 1)
                  ]),
                  company: _withCtx(({ item }) => [
                    _createElementVNode("span", null, _toDisplayString(item.company_name), 1)
                  ]),
                  drivers: _withCtx(({ item }) => [
                    _createElementVNode("span", null, _toDisplayString(item.driver_name), 1)
                  ]),
                  users: _withCtx(({ item }) => [
                    _createElementVNode("span", null, _toDisplayString(item.assignee_name), 1)
                  ]),
                  date: _withCtx(({ item }) => [
                    _createElementVNode("span", null, _toDisplayString(datepickerToUtcDateOnly(item.created_time)), 1)
                  ]),
                  status: _withCtx(({ item }) => [
                    (item.status && getStatus(item.status))
                      ? (_openBlock(), _createBlock(CommonStatus, {
                          key: 0,
                          status: getStatus(item.status)!,
                          tooltipContent: 
                    tooltipContent(item.qa_failed_reason_details)
                  
                        }, null, 8, ["status", "tooltipContent"]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_8, "—"))
                  ]),
                  check: _withCtx(({ item }) => [
                    _createElementVNode("span", {
                      class: _normalizeClass(["qa-status", item.qa_status])
                    }, _toDisplayString(item.qa_status), 3)
                  ]),
                  problems: _withCtx(({ item }) => [
                    _createElementVNode("span", null, _toDisplayString(item.work_type_name), 1)
                  ]),
                  actions: _withCtx(({ item }) => [
                    (item.eld_link)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                          _createVNode(_unref(LinkExternal02), {
                            size: 16,
                            onClick: (e) => goLink(e, item)
                          }, null, 8, ["onClick"])
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["headers", "groups", "loading", "collapsed", "onToggleGroup", "dateIndex", "shiftIndex", "isAdmin", "withoutHeader"]))
              }), 128))
            : _createCommentVNode("", true)
        ], 64))
      }), 128))
    ])
  ]))
}
}

})